'use client';
import React, { useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';

export interface ThreadCounterPillProps {
  threadId: string;
  label?: string;
  variant?: 'default' | 'primary' | 'success';
  className?: string;
  getCounter: (threadId: string) => number;
  subscribe: (callback: () => void) => () => void;
  resetCounter: (threadId: string) => void;
  onPress: () => void;
}

export const ThreadCounterPill: React.FC<ThreadCounterPillProps> = ({
  threadId,
  onPress,
  label = 'New content',
  variant = 'primary',
  className,
  getCounter,
  subscribe,
  resetCounter,
}) => {
  const [count, setCount] = useState(getCounter(threadId));

  useEffect(() => {
    const unsubscribe = subscribe(() => {
      setCount(getCounter(threadId));
    });
    return unsubscribe;
  }, [threadId, getCounter, subscribe]);

  const handlePress = () => {
    resetCounter(threadId);
    onPress();
  };

  if (count === 0) {
    return null;
  }

  const getVariantStyles = () => {
    switch (variant) {
      case 'success':
        return 'bg-green-500 hover:bg-green-600';
      case 'primary':
        return 'bg-blue-500 hover:bg-blue-600';
      default:
        return 'bg-gray-500 hover:bg-gray-600';
    }
  };

  return (
    <div className={twMerge('absolute top-4 left-4 right-4 z-50 flex justify-center', className)}>
      <button
        onClick={handlePress}
        className={twMerge(
          'inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-white text-sm font-medium shadow-lg transition-colors duration-200 min-h-[28px]',
          getVariantStyles(),
        )}>
        <div className="w-1.5 h-1.5 bg-white/90 rounded-full" />
        <span>{count > 1 ? `${count} ${label}s` : label}</span>
      </button>
    </div>
  );
};
